import { useCallback, useEffect, useState } from "react";
import { useGetGridSymbolOrdersQuery } from "@/store/api";

/**
 * GridAdoptedOrphans — subtle, collapsed visibility into "adopted" orphan
 * orders: open LIMIT orders that either carry the grid_level=-1 orphan-
 * recovery sentinel (halted-run BUY inventory re-armed as SELLs in the
 * next run) or rest on a run that is no longer active.
 *
 * Data comes from the existing /api/grid/symbol/{base}/orders endpoint
 * (one probe per symbol); renders nothing when every open order belongs
 * to a live active run with no sentinel orders.
 */

interface AdoptedRow {
  symbol: string;
  runStatus: string;
  count: number;
  notionalUsd: number;
}

function SymbolProbe({ symbol, onResult }: {
  symbol: string;
  onResult: (sym: string, row: AdoptedRow | null) => void;
}) {
  const base = symbol.split("/")[0];
  const { data } = useGetGridSymbolOrdersQuery({ base, fills_limit: 1 });
  useEffect(() => {
    if (!data) return;
    const open: any[] = data.open ?? [];
    const runStatus: string = data.run?.status ?? "";
    // Adopted = the orphan-recovery sentinel (grid_level=-1), or any order
    // still resting on a run that's no longer active (paused/halted).
    const adopted = open.filter(
      (o) => o.grid_level === -1 || (runStatus !== "" && runStatus !== "active"),
    );
    onResult(
      symbol,
      adopted.length === 0
        ? null
        : {
            symbol,
            runStatus: runStatus || "?",
            count: adopted.length,
            notionalUsd: adopted.reduce(
              (a, o) => a + Number(o.price || 0) * Number(o.qty || 0), 0),
          },
    );
  }, [data, symbol, onResult]);
  return null;
}

export function GridAdoptedOrphans({ symbols }: { symbols: string[] }) {
  const [rows, setRows] = useState<Record<string, AdoptedRow | null>>({});
  const onResult = useCallback((sym: string, row: AdoptedRow | null) => {
    setRows((prev) => {
      const old = prev[sym];
      const same =
        old === row ||
        (!!old && !!row && old.count === row.count && old.runStatus === row.runStatus);
      if (same || (!old && !row && sym in prev)) return prev;
      return { ...prev, [sym]: row };
    });
  }, []);

  const found = symbols
    .map((s) => rows[s])
    .filter((r): r is AdoptedRow => !!r);
  const total = found.reduce((a, r) => a + r.count, 0);

  return (
    <>
      {symbols.map((sym) => (
        <SymbolProbe key={sym} symbol={sym} onResult={onResult} />
      ))}
      {found.length > 0 && (
        <details className="rounded-lg border border-amber-500/30 bg-bg2 px-3 py-2">
          <summary className="cursor-pointer text-sm text-muted select-none">
            Adopted orphan orders · <b className="text-fg">{total}</b> open on{" "}
            {found.map((r) => r.symbol).join(", ")}
          </summary>
          <div className="pt-2 space-y-1 text-xs">
            {found.map((r) => (
              <div key={r.symbol} className="flex items-center gap-2">
                <span className="font-mono">{r.symbol}</span>
                <span className="text-muted">
                  {r.count} order{r.count === 1 ? "" : "s"} · $
                  {r.notionalUsd.toFixed(2)} notional · run {r.runStatus}
                </span>
              </div>
            ))}
            <p className="text-muted">
              Fills on these are recorded automatically; cancel via the{" "}
              <code>grid_orphan_sweep_cancel</code> setting.
            </p>
          </div>
        </details>
      )}
    </>
  );
}
